import {
  computeHeadroom,
  formatLimitDisplay,
  hasAnyLimit,
  headroomReserveText,
} from '../lib/limits';
import { headroomLastGoodAtMs, useHeadroom, useHeadroomUnavailable, type HeadroomSnapshot } from '../hooks/useHeadroom';

/**
 * Persistent trader-side status strip showing live headroom against the
 * trader's caps. Mounted in TraderViewLayout so it shows across every trader
 * view (dashboard, positions, trades, etc).
 *
 * Source of truth is GET /api/trader/headroom, which is what the relay's
 * checkOrderLimit uses internally — keeps the widget honest about resting
 * intent + settled-ticker exclusions without making the client re-derive
 * the math from /positions and /pnl.
 *
 * Hides itself entirely when both caps are null so unlimited users don't
 * see clutter. Until the first headroom read lands the strip holds its
 * height with a neutral line instead of appearing late and pushing every
 * trader page down; an unlimited user sees it collapse once (#1041).
 */

const STRIP = 'border-border bg-muted/20 flex items-center gap-4 border-b px-4 py-1.5 text-[11px]';

/** #1113: the hover on the not-refreshing line. */
export const LIMITS_NOT_REFRESHING_TITLE = 'The limits read is failing; the relay still checks your caps on every order.';

/** #1114 T8 quick references, on each BarRow's root div: the harness reads the strip's first span[title] as the rules. */
const POS_TITLE = 'Pos: open positions against your cap.';
const CONT_TITLE = 'Cont: open plus resting-buy contracts against your cap.';

/** Local 24-hour HH:MM:SS (Nadya's ruling for the strip's "as of"). */
function localClock(ms: number): string {
  const d = new Date(ms);
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, '0')).join(':');
}

const BAR_COLOURS: Record<string, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
  over: 'bg-red-600',
  unlimited: 'bg-blue-400',
};

export function LimitsWidget() {
  const head = useHeadroom();
  const unavailable = useHeadroomUnavailable();
  // Read on the render the failed flag or a new snapshot triggers (#1113).
  return <LimitsStrip head={head} unavailable={unavailable} lastGoodAtMs={unavailable ? headroomLastGoodAtMs() : null} />;
}

/**
 * The strip itself, from the headroom read's state (#1113: split out so the
 * Layout Lab can render each live state without the network). `unavailable` is
 * "the latest read failed"; `lastGoodAtMs` the time of the last good read.
 */
export function LimitsStrip({ head, unavailable, lastGoodAtMs }: {
  head: HeadroomSnapshot | null;
  unavailable: boolean;
  lastGoodAtMs: number | null;
}) {
  if (!head) {
    return (
      <div className={STRIP} data-testid="limits-strip-pending">
        <span className="text-muted-foreground font-medium">Limits</span>
        <span className="text-muted-foreground" role="status">
          {unavailable ? 'Limits unavailable' : 'Checking limits…'}
        </span>
      </div>
    );
  }
  const anyCap = hasAnyLimit({
    maxContracts: head.maxContracts,
    maxPositions: head.maxPositions,
  });
  const anyRule =
    head.rulesActive != null &&
    (head.rulesActive.sportRestricted ||
      head.rulesActive.nonSportsBlocked ||
      head.rulesActive.polymarketBlocked ||
      head.rulesActive.polymarketUsBlocked === true);
  if (!anyCap && !anyRule) {
    return null;
  }

  // #1113: a read failed after a good one — the figures on screen are the last good read, not live.
  const notRefreshing = unavailable
    ? `· not refreshing (as of ${lastGoodAtMs == null ? '—' : localClock(lastGoodAtMs)})`
    : '';
  const contractsCurrent = head.openContracts + head.intentContracts;
  const cHead = computeHeadroom(contractsCurrent, head.maxContracts);
  const pHead = computeHeadroom(head.openPositions, head.maxPositions);
  const rulesSuffix = head.rulesActive
    ? [
        head.rulesActive.sportRestricted ? 'sport-restricted' : null,
        head.rulesActive.nonSportsBlocked ? 'no non-sport' : null,
        head.rulesActive.polymarketBlocked ? 'no new Poly' : null,
        head.rulesActive.polymarketUsBlocked ? 'no new Poly US' : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <div className={STRIP}>
      <span className="text-muted-foreground font-medium">Limits</span>
      {anyCap && (
        <>
          <BarRow
            label="Pos"
            hint={POS_TITLE}
            current={head.openPositions}
            cap={head.maxPositions}
            status={pHead.status}
            fraction={pHead.fractionUsed}
            muted={unavailable}
          />
          <BarRow
            label="Cont"
            hint={CONT_TITLE}
            current={contractsCurrent}
            cap={head.maxContracts}
            status={cHead.status}
            fraction={cHead.fractionUsed}
            muted={unavailable}
          />
        </>
      )}
      {head.intentContracts > 0 && anyCap && (
        <span className="text-muted-foreground text-[10px]">
          (incl. {head.intentContracts.toLocaleString()} in resting buys)
        </span>
      )}
      {rulesSuffix && (
        <span className="text-amber-600 dark:text-amber-400 text-[10px]" title={rulesSuffix}>
          · {rulesSuffix}
        </span>
      )}
      {/* #1113: always present and sized to its longest text, so appearing or clearing moves nothing. After
          the rules suffix, and titled only while shown, so the rules line stays the strip's first titled span. */}
      <span
        role="status"
        data-testid="limits-not-refreshing"
        className="text-muted-foreground min-w-[33ch] shrink-0 whitespace-nowrap text-[10px] tabular-nums"
        title={notRefreshing ? LIMITS_NOT_REFRESHING_TITLE : undefined}
      >
        {notRefreshing}
      </span>
    </div>
  );
}

function BarRow({
  label,
  hint,
  current,
  cap,
  status,
  fraction,
  muted = false,
}: {
  label: string;
  /** #1114: the quick reference, on the root div (never a span; see POS_TITLE). */
  hint: string;
  current: number;
  cap: number | null;
  status: string;
  fraction: number | null;
  /** #1113: the figures are the last good read while reads fail. */
  muted?: boolean;
}) {
  const widthPct = cap == null || fraction == null
    ? 100
    : Math.max(0, Math.min(100, fraction * 100));
  const bar = BAR_COLOURS[status] ?? 'bg-muted-foreground';
  return (
    <div className="flex flex-1 items-center gap-2" title={hint}>
      <span className="text-muted-foreground w-8 shrink-0">{label}</span>
      <div className="bg-muted/30 relative h-1.5 max-w-32 flex-1 overflow-hidden rounded">
        <div
          className={`${bar} h-full`}
          style={{ width: `${widthPct}%`, opacity: cap == null || muted ? 0.35 : 1 }}
        />
      </div>
      {/* #1109: one line, with room for "cap / cap" in ch, so a count moving under its cap never resizes it (over
          the cap it may grow). The count sits right-aligned in a cell sized by an invisible copy of the cap, so
          " / cap" stays beside it and the spare room falls at the end, away from the bar. */}
      <span className={`${muted ? 'text-muted-foreground' : 'text-foreground'} shrink-0 whitespace-nowrap tabular-nums`}
        style={{ minWidth: `${headroomReserveText(cap).length}ch` }}
        data-muted={muted || undefined}>
        <span className="inline-grid text-right before:invisible before:col-start-1 before:row-start-1 before:content-[attr(data-reserve)]"
          data-reserve={formatLimitDisplay(cap)}>
          <span className="col-start-1 row-start-1">{current.toLocaleString('en-US')}</span>
        </span>
        {` / ${formatLimitDisplay(cap)}`}
      </span>
    </div>
  );
}
